import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, Navigate } from 'react-router-dom'
import { Spinner } from 'components/loaders'
import { NewHeader as Header } from 'components/page-header'
import { Label } from 'components/indicators/label'
import { useUsers } from '@hooks/useUsers'
import { useEmployees } from '@hooks/useEmployee'
import { useProjectIntegrations } from '@hooks/useProjectIntegrations'
import { useAuth } from '@hooks/useAuth'
import { useChangeApprovalGroup } from '../../../change-approval-group'
import { DetailsSection } from './components/DetailsSection'
import { ApprovalGroupSection } from './components/ApprovalGroupSection'
import { WorkdaySection } from './components/WorkdaySection'
import { IntegrationsSection } from './components/IntegrationsSection'
import { AdminRolesSection } from './components/AdminRolesSection'

export const UserDetails: FC = () => {
  const { t } = useTranslation()
  const { userId } = useParams<{ userId: string }>()
  const { user: signedInUser } = useAuth()
  const { data: users, isLoading: usersLoading } = useUsers()
  const { data: employees, isLoading: employeesLoading } = useEmployees()
  const { data: projectIntegrations, isLoading: integrationsLoading } =
    useProjectIntegrations()
  const { changeApprovalGroup } = useChangeApprovalGroup()

  const user = useMemo(
    () => users?.find((u) => u.id === Number(userId)),
    [users, userId],
  )
  const employee = useMemo(
    () => employees?.find((e) => e.user.id === Number(userId)),
    [employees, userId],
  )

  if (usersLoading || employeesLoading || integrationsLoading) {
    return <Spinner />
  }

  if (!user || !projectIntegrations) {
    return <Navigate to="/users" replace />
  }

  // Mirrors the Users table: the approval group is only editable when the
  // signed-in user administers the user's latest facility.
  const canEditApprovalGroup = Boolean(
    signedInUser?.administeredFacilities.some(
      ({ id }) => id === user.latestFacility?.id,
    ),
  )

  return (
    <div className="flex flex-col flex-grow">
      <Header
        title={user.name}
        renderTitleSlot={() => (
          <Label color={user.active ? 'success' : 'neutral'}>
            {user.active ? t('common.active') : t('common.inactive')}
          </Label>
        )}
      />
      <div className="flex flex-col flex-grow min-w-0 p-8">
        <DetailsSection user={user} />
        <WorkdaySection
          userName={user.name}
          workdayWorkerId={user.workdayWorkerId}
          hireDate={employee?.hireDate}
          terminationDate={employee?.terminationDate}
        />
        <IntegrationsSection
          projectIntegrations={projectIntegrations}
          integrationUserLinks={user.integrationUserLinks}
        />
        <ApprovalGroupSection
          employee={employee}
          onEdit={
            canEditApprovalGroup && employee
              ? () => changeApprovalGroup(employee)
              : undefined
          }
        />
        {/* Admin roles are only visible to — and manageable by — super admins */}
        {signedInUser?.superAdmin && (
          <AdminRolesSection
            user={user}
            isSignedInUser={signedInUser.id === user.id}
          />
        )}
      </div>
    </div>
  )
}
